import type { SPIntl } from 'contexts/intl/intlContext';
import type { Locale } from 'date-fns';
import type { MapDispatchToPropsFunction, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  batchExportExcelThunk,
  batchQuotesAbortedThunk,
  resetSelectedBatchFormToDefaultDatesThunk,
  selectedBatchFormsCloneRequestedThunk,
  selectedBatchFormsOpenInQuoteWorkspaceRequestedThunk,
  selectedBatchFormToQuoteThunk,
} from 'state/batchForm/batchForm.thunks';
import {
  canAbortSelectedFormsInBatchTab,
  canCloneSelectedFormsInBatchTab,
  canDeleteSelectedFormsInBatchTab,
  canExportSelectedFormsInBatchTab,
  canOpenApplyBatchForm,
  canOpenInQuoteWorkspaceSelectedFormsInBatchTab,
  canPriceSelectedFormsInBatchTab,
  canRefreshSelectedFormsDatesInBatchTab,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import {
  batchToggleDeleteFormConfirmationModal,
  toggleBatchWorkspaceMultipleApply,
} from 'state/workspaces/batch/tabContent/tabContent.actions';

import type {
  BatchToolbarFormActionsDispatchProps,
  BatchToolbarFormActionsOwnProps,
  BatchToolbarFormActionsStateProps,
  BatchToolbarFormActionsTrigger,
} from './BatchToolbarFormActions.models';

export const mapStateToProps: MapStateToProps<
  BatchToolbarFormActionsStateProps,
  BatchToolbarFormActionsOwnProps,
  AppState
> = (state: AppState, { tabId }: BatchToolbarFormActionsOwnProps): BatchToolbarFormActionsStateProps => ({
  canDeleteSelectedItems: canDeleteSelectedFormsInBatchTab(state, tabId),
  canExportSelectedItems: canExportSelectedFormsInBatchTab(state, tabId),
  canDuplicateSelectedItems: canCloneSelectedFormsInBatchTab(state, tabId),
  canPriceSelectedItems: canPriceSelectedFormsInBatchTab(state, tabId),
  canRefreshSelectedItemsDates: canRefreshSelectedFormsDatesInBatchTab(state, tabId),
  canAbortSelectedItems: canAbortSelectedFormsInBatchTab(state, tabId),
  canOpenApplyBatchForm: canOpenApplyBatchForm(state, tabId),
  canOpenInQuoteWorkspace: canOpenInQuoteWorkspaceSelectedFormsInBatchTab(state, tabId),
});

const workSpaceString = 'Batch Workspace';
export const mapDispatchToProps: MapDispatchToPropsFunction<
  BatchToolbarFormActionsDispatchProps,
  BatchToolbarFormActionsOwnProps
> = (dispatch: ThunkDispatch, ownProps: BatchToolbarFormActionsOwnProps): BatchToolbarFormActionsDispatchProps => ({
  onDelete(trigger: BatchToolbarFormActionsTrigger = 'button') {
    logAnalytics.trackEvent(
      workSpaceString,
      trigger === 'shortcut' ? 'Delete selected form (Ctrl+Delete)' : 'Delete from header',
    );
    dispatch(batchToggleDeleteFormConfirmationModal(ownProps.tabId, true));
  },
  onExportInternalExcel(intl: SPIntl, locale: Locale) {
    logAnalytics.trackEvent(workSpaceString, 'Export internal excel from header');
    dispatch(batchExportExcelThunk(ownProps.tabId, true, intl, locale));
  },
  onExportExternalExcel(intl: SPIntl, locale: Locale) {
    logAnalytics.trackEvent(workSpaceString, 'Export external excel from header');
    dispatch(batchExportExcelThunk(ownProps.tabId, false, intl, locale));
  },
  onDuplicate(trigger: BatchToolbarFormActionsTrigger = 'button') {
    logAnalytics.trackEvent(
      workSpaceString,
      trigger === 'shortcut' ? 'Duplicate form (Ctrl+M)' : 'Duplicate from header',
    );
    dispatch(selectedBatchFormsCloneRequestedThunk(ownProps.tabId));
  },
  onPrice(trigger: BatchToolbarFormActionsTrigger = 'button') {
    logAnalytics.trackEvent(
      workSpaceString,
      trigger === 'shortcut' ? 'Price selected form (Ctrl+Enter)' : 'Price from header',
    );
    dispatch(selectedBatchFormToQuoteThunk(ownProps.tabId));
  },
  onResetDates() {
    logAnalytics.trackEvent(workSpaceString, 'Refresh dates from header');
    dispatch(resetSelectedBatchFormToDefaultDatesThunk(ownProps.tabId));
  },
  onAbort() {
    logAnalytics.trackEvent(workSpaceString, 'Cancel quote(s) from header');
    dispatch(batchQuotesAbortedThunk(ownProps.tabId));
  },
  toggleBatchMultiplyApply() {
    logAnalytics.trackEvent(workSpaceString, 'Open batch apply modal');
    dispatch(toggleBatchWorkspaceMultipleApply(ownProps.tabId, true));
  },
  onOpenInQuoteWorkspace() {
    logAnalytics.trackEvent(workSpaceString, 'Open quote(s) in Quote Workspace from header');
    dispatch(selectedBatchFormsOpenInQuoteWorkspaceRequestedThunk(ownProps.tabId));
  },
});

export const connectBatchToolbarFormActions = connect<
  BatchToolbarFormActionsStateProps,
  BatchToolbarFormActionsDispatchProps,
  BatchToolbarFormActionsOwnProps,
  AppState
>(mapStateToProps, mapDispatchToProps);
